import React, { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { fmtDate as formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarPlus, Undo2 } from "lucide-react";
import { Panel } from "@/components/inquiries/OverviewCharts";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const ConfirmDialog = ({ open, onOpenChange, title, description, confirmLabel, onConfirm }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent data-testid="ext-undo-dialog">
      <AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle><AlertDialogDescription>{description}</AlertDialogDescription></AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="ext-undo-cancel">Cancel</AlertDialogCancel>
        <AlertDialogAction data-testid="ext-undo-confirm" onClick={onConfirm} className="bg-blue-700 hover:bg-blue-800">{confirmLabel}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const optKey = (o) => `${o.compliance_key}|${o.standard_due_date}`;

const ApplyForm = ({ options, onDone }) => {
  const [sel, setSel] = useState("");
  const [f, setF] = useState({ new_due_date: "", reference: "", notes: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const o = options.find((x) => optKey(x) === sel);
  useEffect(() => {
    setPreview(null);
    if (!o) return;
    setF((x) => ({ ...x, new_due_date: o.extended_to[0] || o.standard_due_date }));
    api.get("/compliance/shift/preview", { params: { compliance_key: o.compliance_key, standard_due_date: o.standard_due_date } })
      .then((r) => setPreview(r.data)).catch((e) => toast.error(formatErr(e)));
  }, [sel]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/compliance/shift", { compliance_key: o.compliance_key, standard_due_date: o.standard_due_date, ...f });
      toast.success(`${o.name}: moved to ${formatDate(data.new_due_date)} for ${data.updated} client(s)`);
      setSel(""); setF({ new_due_date: "", reference: "", notes: "" }); onDone();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Apply a government extension" sub="Moves the due date of one filing period for every client in your scope. The next period goes back to the normal date." testid="ext-form">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Filing and period</Label>
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger data-testid="ext-filing" className="mt-1"><SelectValue placeholder="Choose a filing and its standard due date" /></SelectTrigger>
            <SelectContent>
              {options.map((x) => (
                <SelectItem key={optKey(x)} value={optKey(x)}>
                  {x.name} · due {formatDate(x.standard_due_date)} · {x.clients} client(s){x.extended_to.length ? ` · extended to ${x.extended_to.map(formatDate).join(", ")}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">New due date</Label>
          <Input data-testid="ext-new-date" type="date" value={f.new_due_date} onChange={set("new_due_date")} disabled={!o} className="mt-1 mono" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Notification / circular reference</Label>
          <Input data-testid="ext-reference" value={f.reference} onChange={set("reference")} disabled={!o} placeholder="e.g. CBDT Circular No. 9/2026" className="mt-1" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
          <Textarea data-testid="ext-notes" rows={2} value={f.notes} onChange={set("notes")} disabled={!o} className="mt-1" />
        </div>
      </div>
      {o && (
        <div className="mt-4 text-xs text-slate-600 bg-blue-50/60 border border-blue-100 rounded-md p-3" data-testid="ext-preview">
          {preview ? <>Applies to <b>{preview.count}</b> open filing(s): {preview.items.map((i) => i.entity || "—").join(", ")}</> : "Checking affected clients…"}
        </div>
      )}
      <div className="mt-4 flex justify-end">
        <Button data-testid="ext-apply" disabled={busy || !o || !f.new_due_date || !preview?.count || f.new_due_date === o?.standard_due_date}
          onClick={submit} className="bg-blue-700 hover:bg-blue-800 text-white gap-2">
          <CalendarPlus className="w-4 h-4" />{busy ? "Applying…" : `Extend for ${preview?.count ?? "…"} client(s)`}
        </Button>
      </div>
    </Panel>
  );
};

const History = ({ rows, onUndo }) => (
  <Panel title="Extension history" sub="Undo puts each client back to the date it had before, unless it has been filed or moved since." testid="ext-history">
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[760px]">
        <thead className="text-[11px] uppercase tracking-wider text-slate-500"><tr className="border-b border-slate-100">
          <th className="text-left py-2">Filing</th><th className="text-left px-2">Standard</th><th className="text-left px-2">Moved to</th>
          <th className="text-left px-2">Reference</th><th className="text-left px-2">Clients</th><th className="text-left px-2">By</th><th /></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`ext-row-${r.id}`} className={`border-b border-slate-50 last:border-0 align-top ${r.undone_at || r.superseded ? "text-slate-500 bg-slate-50/60" : ""}`}>
              <td className="py-2.5 pr-2"><div className="font-medium text-slate-900">{r.name}</div>{r.notes && <div className="text-[11px] text-slate-500">{r.notes}</div>}</td>
              <td className="px-2 mono text-xs">{formatDate(r.standard_due_date)}</td>
              <td className="px-2 mono text-xs">{r.kind === "restore" ? "Restored" : formatDate(r.new_due_date)}</td>
              <td className="px-2 text-xs">{r.reference || "—"}</td>
              <td className="px-2 text-xs max-w-[220px]" data-testid={`ext-clients-${r.id}`}><b>{r.clients.length}</b> · {r.clients.slice(0, 3).join(", ")}{r.clients.length > 3 ? ` +${r.clients.length - 3} more` : ""}</td>
              <td className="px-2 text-xs">{r.created_by_name}<div className="text-[11px] text-slate-400">{new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</div></td>
              <td className="pl-2 text-right">
                {r.superseded ? <span className="text-xs text-slate-600" data-testid={`ext-superseded-${r.id}`}>Replaced by a later change</span>
                  : r.undone_at ? <span className="text-xs text-slate-600" data-testid={`ext-undone-${r.id}`}>Undone by {r.undone_by_name} ({r.undo_restored} restored{r.undo_skipped ? `, ${r.undo_skipped} skipped` : ""})</span>
                  : r.can_undo && <Button size="sm" variant="outline" data-testid={`ext-undo-${r.id}`} onClick={() => onUndo(r)} className="h-7 gap-1 text-xs"><Undo2 className="w-3.5 h-3.5" />Undo</Button>}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-500">No extensions applied yet.</td></tr>}
        </tbody>
      </table>
    </div>
  </Panel>
);

export default function ComplianceExtensions() {
  const [options, setOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [undo, setUndo] = useState(null);
  const load = useCallback(() => {
    api.get("/compliance/extensions/options").then((r) => setOptions(r.data)).catch((e) => toast.error(formatErr(e)));
    api.get("/compliance/extensions").then((r) => setRows(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const doUndo = async () => {
    try {
      const { data } = await api.post(`/compliance/extensions/${undo.id}/undo`);
      toast.success(`Undone: ${data.restored} restored${data.skipped ? `, ${data.skipped} skipped (filed or moved since)` : ""}`);
      load();
    } catch (e) { toast.error(formatErr(e)); } finally { setUndo(null); }
  };
  return (
    <div data-testid="compliance-extensions-page" className="space-y-5">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Compliance</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Due date extensions</h1>
      </div>
      <ApplyForm options={options} onDone={load} />
      <History rows={rows} onUndo={setUndo} />
      <ConfirmDialog open={!!undo} onOpenChange={(v) => !v && setUndo(null)} title="Undo this extension?"
        description={undo ? `${undo.name} (standard ${formatDate(undo.standard_due_date)}) goes back to its previous date for clients not yet filed.` : ""}
        confirmLabel="Undo extension" onConfirm={doUndo} />
    </div>
  );
}
